import {
  ChatItem,
  MessageSource,
  ToolCallStatus,
} from "features/assistant/definitions";
import { HistoryMessage } from "services/assistant/websocket";

const mapHistoryStatus = (status?: string): ToolCallStatus => {
  if (status === "completed") return "completed";
  if (status === "running") return "running";
  return "error";
};

/**
 * Converts MESSAGE_HISTORY_SNAPSHOT messages into a flat ChatItem[] array.
 * Text blocks become message items; tool_use blocks become independent tool_call items.
 * Consecutive text blocks within the same HistoryMessage are merged into one message
 * item, EXCEPT when their source differs — an "answers" block after an "assistant" block
 * starts a new message item so the Answers content stays a distinct, verbatim message.
 */
export const convertHistoryMessages = (
  historyMessages: HistoryMessage[],
): ChatItem[] => {
  const items: ChatItem[] = [];

  for (const msg of historyMessages) {
    let pendingText: string | null = null;
    let pendingDisplayText: string | undefined;
    let pendingSource: MessageSource = "assistant";

    const flushText = () => {
      if (pendingText !== null) {
        items.push({
          type: "message",
          id: crypto.randomUUID(),
          role: msg.role,
          content: pendingText,
          displayContent: pendingDisplayText,
          isStreaming: false,
          source: pendingSource,
        });
        pendingText = null;
        pendingDisplayText = undefined;
        pendingSource = "assistant";
      }
    };

    for (const block of msg.content) {
      if (block.type === "text") {
        const blockSource: MessageSource = block.source ?? "assistant";
        if (pendingText !== null && blockSource === pendingSource) {
          pendingText += `\n\n${block.text || ""}`;
        } else {
          flushText();
          pendingText = block.text || "";
          pendingDisplayText = block.display_text || undefined;
          pendingSource = blockSource;
        }
      } else if (block.type === "tool_use") {
        flushText();
        items.push({
          type: "tool_call",
          id: crypto.randomUUID(),
          toolUseId: crypto.randomUUID(),
          toolName: block.tool_name || "unknown",
          status: mapHistoryStatus(block.status),
          description: block.description,
        });
      } else {
        console.warn("Unknown history content block type:", block.type);
      }
    }

    flushText();
  }

  return items;
};
